/**
 * EvidenceModal — shows diagnostic accuracy (sens/spec/LR+/LR−) for a special test.
 * Iter 72.
 *
 * Opens when the clinician taps a reference badge in the SmartSelect picker.
 * Each metric gets a colored bar (green = strong, amber = moderate, red = weak)
 * and a one-sentence clinical interpretation.
 *
 * Props:
 *   testName  string — must match a key in TEST_ACCURACY
 *   onClose   () => void
 */
import React from 'react';
import { X, BookOpen, ExternalLink, TrendingUp, TrendingDown, Target, ShieldCheck } from 'lucide-react';
import { TEST_ACCURACY, strengthOf } from '../lib/test-accuracy';

const COLOR = {
  strong:   { bg: '#E3EFE7', fg: '#1F6043', dot: '#10A862' },
  moderate: { bg: '#FFF4D6', fg: '#8A5A00', dot: '#FFB000' },
  weak:     { bg: '#F3E0DF', fg: '#8A2A2A', dot: '#B83A3A' },
  unknown:  { bg: '#F4EDDC', fg: '#6B7280', dot: '#9CA3AF' },
};

function MetricBar({ label, value, kind, helpText, icon: Icon }) {
  const strength = strengthOf(kind, value);
  const c = COLOR[strength];
  // Visual fill: sens/spec are 0-1 (×100); LR+ 0-30 cap, LR- inverse
  let pct = 0;
  if (kind === 'sens' || kind === 'spec') pct = (value || 0) * 100;
  else if (kind === 'lr_pos') pct = Math.min(100, (value || 0) * 5);     // LR+ 20 = full
  else if (kind === 'lr_neg') pct = Math.max(0, 100 - (value || 1) * 100); // LR− 0 = full, 1 = empty
  return (
    <div className="mb-3" data-testid={`evidence-metric-${kind}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5" style={{ color: c.fg }}>
          <Icon className="w-3.5 h-3.5"/> {label}
        </span>
        <span className="text-base font-mono font-bold" style={{ color: c.fg }}>
          {value == null ? '—' : (kind.startsWith('lr_') ? value.toFixed(2) : (value * 100).toFixed(0) + '%')}
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: c.bg }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: c.dot }}/>
      </div>
      <div className="text-[10px] mt-1" style={{ color: c.fg }}>{helpText}</div>
    </div>
  );
}

export default function EvidenceModal({ testName, onClose }) {
  const data = TEST_ACCURACY[testName];
  if (!data) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid="evidence-modal-empty">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
          <div className="text-fg-muted text-sm mb-3">No diagnostic-accuracy data on file for <strong>{testName}</strong>.</div>
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
        </div>
      </div>
    );
  }
  const sensInterp = data.sens >= 0.85 ? '"SnNout" — negative result rules condition OUT.'
                  : data.sens >= 0.70 ? 'Moderate screen — useful but not definitive when negative.'
                  : 'Weak screen — a negative test does not exclude the condition.';
  const specInterp = data.spec >= 0.85 ? '"SpPin" — positive result rules condition IN.'
                  : data.spec >= 0.70 ? 'Moderate — positive raises probability but consider differential.'
                  : 'Weak — positive may have many causes.';
  const lrPosInterp = data.lr_pos >= 10 ? 'Large, conclusive shift toward diagnosis when positive.'
                   : data.lr_pos >= 5  ? 'Moderate, useful shift when positive.'
                   : data.lr_pos >= 2  ? 'Small shift — combine with other tests.'
                   : 'Minimal — positive test does little to change probability.';
  const lrNegInterp = data.lr_neg <= 0.1 ? 'Large shift away from diagnosis when negative — strong rule-out.'
                   : data.lr_neg <= 0.2 ? 'Moderate rule-out value when negative.'
                   : data.lr_neg <= 0.5 ? 'Small rule-out value — combine with sensitivity.'
                   : 'Minimal — a negative result does little to lower probability.';

  return (
    <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid="evidence-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-soft flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-[#003D80] text-white flex items-center justify-center">
              <BookOpen className="w-4 h-4"/>
            </div>
            <div>
              <h3 className="font-heading font-bold text-[#0F1B2D] text-base">{testName}</h3>
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">Screening: {data.condition}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg" data-testid="evidence-modal-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <MetricBar label="Sensitivity"        value={data.sens}    kind="sens"    icon={TrendingDown} helpText={sensInterp}/>
          <MetricBar label="Specificity"        value={data.spec}    kind="spec"    icon={Target}       helpText={specInterp}/>
          <MetricBar label="Likelihood ratio +" value={data.lr_pos}  kind="lr_pos"  icon={TrendingUp}   helpText={lrPosInterp}/>
          <MetricBar label="Likelihood ratio −" value={data.lr_neg}  kind="lr_neg"  icon={ShieldCheck}  helpText={lrNegInterp}/>

          {data.notes && (
            <div className="mt-4 p-3 rounded-lg bg-[#F2EBDB] text-xs leading-relaxed text-[#0F1B2D]" data-testid="evidence-notes">
              <strong className="text-[#003D80]">Clinical interpretation: </strong>{data.notes}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-soft">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Source</div>
            <div className="text-xs text-fg leading-relaxed">{data.refFull}</div>
            {data.refUrl && (
              <a href={data.refUrl} target="_blank" rel="noreferrer"
                 className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                 data-testid="evidence-source-link">
                <ExternalLink className="w-3 h-3"/> Open on PubMed
              </a>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-soft bg-bg-secondary/30 text-center">
          <div className="text-[10px] text-fg-muted leading-relaxed">
            Diagnostic accuracy ≠ guaranteed diagnosis. Always integrate with patient history, multiple tests in a cluster, and imaging where indicated.
          </div>
        </div>
      </div>
    </div>
  );
}
